import { useEffect, useState } from "react";
import { Text } from "react-native";
import { View } from "ui/atoms/view";
import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import {
  CHOICE_TYPES,
  QuestionTypeDescriptionMapping,
  QuestionTypeEnum,
} from "app/services/api/types/forms/enums/QuestionTypeEnum";
import { Button, ButtonText } from "ui/gluestack/button";
import { QuestionOptionModel } from "app/services/api/types/forms/models/QuestionOptionModel";
import { QuestionModel } from "app/services/api/types/forms/models/QuestionModel";
import { useForm } from "react-hook-form";
import { QuestionInstructionModel } from "app/services/api/types/forms/models/QuestionInstructionModel";
import { QuestionTypeSelectionMolecule } from "../molecules/QuestionTypeSelectionMolecule";
import { QuestionDetailsFormMolecule } from "../molecules/QuestionDetailsFormMolecule";

type Step = "type" | "form";

export type QuestionEditFormValues = {
  id?: number;
  content: string;
  type?: QuestionTypeEnum;
  required: boolean;
  min_value: string; // collect as string, parse on submit
  max_value: string; // collect as string, parse on submit
  unit?: string;
  options: QuestionOptionModel[];
  instructions: QuestionInstructionModel[];
};

export interface QuestionEditModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSubmit: (question: Partial<QuestionModel>) => void;
  initialQuestion?: Partial<QuestionModel>;
}

export default function QuestionEditModal({
  isVisible,
  onClose,
  onSubmit,
  initialQuestion,
}: QuestionEditModalProps) {
  const [step, setStep] = useState<Step>("type");

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<QuestionEditFormValues>({
    defaultValues: {
      id: undefined,
      type: undefined,
      content: "",
      required: false,
      unit: "",
      min_value: "",
      max_value: "",
      options: [],
      instructions: [],
    },
  });

  const type = watch("type");

  const resetToInitialQuestion = () => {
    if (initialQuestion) {
      setStep("form");

      reset({
        id: initialQuestion.id,
        type: initialQuestion.type,
        content: initialQuestion.content ?? "",
        required: !!initialQuestion.required,
        unit: initialQuestion.unit ?? "",
        min_value: String(initialQuestion.min_value ?? ""),
        max_value: String(initialQuestion.max_value ?? ""),
        options: initialQuestion.options ?? [],
        instructions: initialQuestion.instructions ?? [],
      });
      clearErrors();
    }
  };

  const resetToBlankQuestion = () => {
    setStep("type");

    reset({
      type: undefined,
      content: "",
      required: false,
      unit: "",
      min_value: "",
      max_value: "",
      options: [],
      instructions: [],
    });
    clearErrors();
  };

  useEffect(() => {
    if (isVisible && initialQuestion) {
      // Prefill for edit mode and go straight to form
      resetToInitialQuestion();
    } else {
      // Fresh create state
      resetToBlankQuestion();
    }
  }, [isVisible, initialQuestion]);

  const handlePickType = (t: QuestionTypeEnum) => {
    setValue("type", t);
    setStep("form");
  };

  // Validation helpers
  const isNumberType = (t?: QuestionTypeEnum) => t === QuestionTypeEnum.NUMBER;
  const isChoiceType = (t?: QuestionTypeEnum) =>
    t ? CHOICE_TYPES.includes(t) : false;

  const validateTypeSelected = (values: QuestionEditFormValues) => {
    if (!values.type) {
      throw new Error("Please select a question type.");
    }
  };

  const validateAndParseNumberBounds = (
    values: QuestionEditFormValues,
  ): { min: number | null; max: number | null } | null => {
    if (!isNumberType(values.type)) return null;

    let min: number | null = null;
    let max: number | null = null;

    const minStr = values.min_value?.trim() ?? "";
    const maxStr = values.max_value?.trim() ?? "";

    if (minStr !== "") {
      const parsed = Number(minStr);
      if (Number.isNaN(parsed)) {
        throw new Error("Minimum value must be a valid number.");
      }
      min = parsed;
    }

    if (maxStr !== "") {
      const parsed = Number(maxStr);
      if (Number.isNaN(parsed)) {
        throw new Error("Maximum value must be a valid number.");
      }
      max = parsed;
    }

    if (min !== null && max !== null && max < min) {
      throw new Error(
        "Maximum value must be greater than or equal to minimum value.",
      );
    }

    return { min, max };
  };

  const validateChoiceOptions = (values: QuestionEditFormValues) => {
    if (!isChoiceType(values.type)) return true;
    if (!values.options || values.options.length === 0) {
      throw new Error("Please add at least one option.");
    }
    if (values.options.some((o) => !o.title?.trim())) {
      throw new Error("All options must have a title.");
    }
  };

  // No custom updater needed as we use Controller-bound inputs for options

  const onSubmitForm = handleSubmit((values) => {
    try {
      clearErrors();

      validateTypeSelected(values);
      validateChoiceOptions(values);

      const bounds = validateAndParseNumberBounds(values);

      const payload: Partial<QuestionModel> = {
        content: values.content.trim(),
        type: values.type!,
        required: values.required,
        unit: values.unit?.trim() ? values.unit.trim() : undefined,
        min_value: bounds ? bounds.min : null,
        max_value: bounds ? bounds.max : null,
        options: isChoiceType(values.type) ? values.options : null,
        instructions: values.instructions,
      };

      onSubmit(payload);
    } catch (error) {
      setError("root", {
        type: "manual",
        message: error.message,
      });
    }
  });

  const isEditing = !!initialQuestion;

  const title =
    step === "type"
      ? "Select a question type"
      : type
        ? `${isEditing ? "Edit" : "New"} ${QuestionTypeDescriptionMapping[type].short} question`
        : isEditing
          ? "Edit question"
          : "New question";

  return (
    <ReusableModal
      isVisible={isVisible}
      onClose={onClose}
      title={title}
      description={
        step === "type"
          ? "Choose what kind of question you want to add."
          : "Fill in the details for your question."
      }
      className="max-w-[800px] w-full"
    >
      {step === "type" ? (
        <QuestionTypeSelectionMolecule onPickType={handlePickType} />
      ) : (
        <View className="gap-4">
          <QuestionDetailsFormMolecule control={control} type={type} />

          {errors?.root?.message ? (
            <Text className="text-sm text-red-600">{errors.root.message}</Text>
          ) : null}

          <View className="flex flex-row justify-between gap-3 pt-2">
            <Button
              variant="outline"
              action="secondary"
              onPress={() =>
                isEditing || step === "form" ? setStep("type") : onClose()
              }
            >
              <ButtonText>
                {isEditing || step === "form" ? "Change type" : "Close"}
              </ButtonText>
            </Button>
            <Button variant="solid" action="primary" onPress={onSubmitForm}>
              <ButtonText>Save</ButtonText>
            </Button>
          </View>
        </View>
      )}
    </ReusableModal>
  );
}
